import { MAX_STEPS_PER_FRAME, STEP_DT, STROKE_T } from '../engine/constants';
import type { Level, Pt } from '../engine/level';
import { isBlocked } from '../engine/stroke';
import { GameWorld } from '../engine/world';
import { attachInput } from './input';
import { Renderer } from './renderer';
import { fitRotation, watchViewport } from './orient';
import { answersOn } from './progress';

export type PlayEvents = {
  onCleared(id: number): void;
  onBack(): void;
  onNext(id: number): void;
  hasNext(id: number): boolean;
};

/** 한 판의 화면: 캔버스 + 상단 버튼 + 오버레이. destroy() 로 정리. */
export class PlayScreen {
  private world: GameWorld;
  private renderer: Renderer;
  private drawing: { pts: Pt[]; blocked: boolean[]; invalid: boolean; /** 간격을 둔 점열(꼬임 검사용) */ spaced: Pt[] } | null = null;
  private acc = 0;
  private lastT = 0;
  private raf = 0;
  private detach: () => void;
  private unwatch: () => void = () => {};
  private root: HTMLElement;
  private overlay: HTMLElement;
  private hint: HTMLElement;
  private shownStatus: string = 'playing';
  readonly debug: boolean;

  constructor(private host: HTMLElement, private level: Level, private ev: PlayEvents, opts: { debug?: boolean } = {}) {
    this.debug = !!opts.debug;
    this.world = new GameWorld(level);
    this.root = document.createElement('div');
    this.root.className = 'play';
    this.root.innerHTML = `
      <canvas class="board"></canvas>
      <div class="hud">
        <button class="icon back" aria-label="목록">‹</button>
        <div class="title">LEVEL ${String(level.id).padStart(3, '0')}${answersOn() ? ' <button class="ans">해답</button>' : ''}</div>
        <button class="icon retry" aria-label="다시">↻</button>
      </div>
      <div class="hint" hidden></div>
      <div class="overlay" hidden>
        <div class="card">
          <div class="big">CLEAR</div>
          <div class="row">
            <button class="btn again">다시</button>
            <button class="btn next">다음</button>
          </div>
        </div>
      </div>
      <div class="answer" hidden><img alt="해답" /><div class="close">닫기</div></div>
      <div class="dbg" hidden></div>`;
    host.replaceChildren(this.root);
    const canvas = this.root.querySelector<HTMLCanvasElement>('canvas')!;
    this.overlay = this.root.querySelector<HTMLElement>('.overlay')!;
    this.hint = this.root.querySelector<HTMLElement>('.hint')!;
    this.renderer = new Renderer(canvas);

    this.root.querySelector('.back')!.addEventListener('click', () => ev.onBack());
    const ansBox = this.root.querySelector<HTMLElement>('.answer')!;
    this.root.querySelector('.ans')?.addEventListener('click', () => {
      ansBox.querySelector('img')!.src = `/answers/${level.id}.jpg`;
      ansBox.hidden = false;
    });
    ansBox.addEventListener('click', () => { ansBox.hidden = true; });
    this.root.querySelector('.retry')!.addEventListener('click', () => this.restart());
    this.root.querySelector('.again')!.addEventListener('click', () => this.restart());
    const next = this.root.querySelector<HTMLButtonElement>('.next')!;
    next.hidden = !ev.hasNext(level.id);
    next.addEventListener('click', () => ev.onNext(level.id + 1));

    this.detach = attachInput(canvas, (x, y) => this.renderer.vp.toWorld(x, y), {
      onStart: (p) => this.start(p),
      onMove: (p) => this.move(p),
      onEnd: () => this.end(),
    });

    this.unwatch = watchViewport(this.fit);
    document.addEventListener('visibilitychange', this.onVis);
    this.lastT = performance.now();
    this.raf = requestAnimationFrame(this.frame);
  }

  private fit = () => {
    const [w, h] = fitRotation(this.root);
    this.renderer.resize(w, h, window.devicePixelRatio || 1);
    this.render();
  };

  private onVis = () => { this.acc = 0; this.lastT = performance.now(); };

  private start(p: Pt) {
    if (this.world.status !== 'playing') return;
    this.drawing = { pts: [], blocked: [], invalid: false, spaced: [] };
    this.move(p);
  }
  private move(p: Pt) {
    if (!this.drawing || this.drawing.invalid) return;
    if (isBlocked(p, this.world.blockers(), STROKE_T / 2)) {
      // 땅·공에 닿음 → 거기서 손을 뗀 것처럼 지금까지의 선을 바로 만든다. 손을 뗄 때까지 나머지는 무시.
      const pts = this.drawing.pts;
      this.drawing = { pts: [], blocked: [], invalid: true, spaced: [] };
      if (pts.length) this.world.addStroke(pts);
      return;
    }
    this.drawing.pts.push(p);
    this.drawing.blocked.push(false);
    if (isBlocked(p, this.world.strokeBlockers(), STROKE_T / 2)) this.drawing.invalid = true; // 이미 놓인 선을 지남 → 취소
    // 자기 교차(동글동글 말기)는 허용한다 — 사용자 요청
  }
  private end() {
    if (!this.drawing) return;
    const { pts, invalid } = this.drawing;
    this.drawing = null;
    if (this.world.status === 'playing' && pts.length && !invalid) this.world.addStroke(pts);
  }

  restart() {
    this.world = new GameWorld(this.level);
    this.drawing = null;
    this.acc = 0;
    this.shownStatus = 'playing';
    this.overlay.hidden = true;
    this.hint.hidden = true;
    this.render();
  }

  private frame = (t: number) => {
    this.raf = requestAnimationFrame(this.frame);
    const dt = Math.min((t - this.lastT) / 1000, MAX_STEPS_PER_FRAME * STEP_DT);
    this.lastT = t;
    this.acc += dt;
    let n = 0;
    while (this.acc >= STEP_DT && n < MAX_STEPS_PER_FRAME) { this.world.step(); this.acc -= STEP_DT; n++; }
    if (this.acc > STEP_DT) this.acc = 0;
    this.render();
    this.syncStatus();
  };

  private render() {
    this.renderer.draw(this.world.snapshot(), this.drawing);
    if (this.debug) {
      const d = this.root.querySelector<HTMLElement>('.dbg')!;
      d.hidden = false;
      const vp = this.renderer.vp;
      d.textContent = `dpr ${window.devicePixelRatio} vv ${window.visualViewport?.width}x${window.visualViewport?.height} canvas ${vp.w}x${vp.h} scale ${vp.scale.toFixed(1)} strokes ${this.world.strokes.length} t ${this.world.time.toFixed(1)}`;
    }
  }

  private syncStatus() {
    const s = this.world.status;
    if (s === this.shownStatus) return;
    this.shownStatus = s;
    if (s === 'cleared') {
      this.drawing = null;
      this.overlay.hidden = false;
      this.ev.onCleared(this.level.id);
    } else if (s === 'lost') {
      this.hint.textContent = '공이 떨어졌어요 — ↻ 로 다시';
      this.hint.hidden = false;
    }
  }

  destroy() {
    cancelAnimationFrame(this.raf);
    this.detach();
    this.unwatch();
    document.removeEventListener('visibilitychange', this.onVis);
    this.root.remove();
  }
}
